// The trameria iOS app's own notifications (APNs), as the screen reaches
// them: through the outside's bridge, where the app says it shows them
// (`ShellHostInfo.notices`).
//
// Promise:
// - `appNoticeHost()` is null outside the app (a browser, the desktop app).
// - `readAppNotices` asks the app how its notifications stand and resolves
//   with its answer, or null when none comes within a few seconds (an older
//   app). With `ask`, the app first asks the person when they were never
//   asked (the answer comes once they chose), or opens its notification
//   settings when they refused before (the answer comes at once; what they
//   change there comes later on its own).
import { createWindowShellHost } from "../shell/outside/bridge";
import type {
  ShellHostNotices,
  ShellNoticesState,
} from "../shell/outside/contract";
import { SHELL_CONTRACT_VERSION } from "../shell/outside/contract";
import type { ShellHost } from "../shell/outside/outside";

export type AppNoticeHost = ShellHost & { notices: ShellHostNotices };

const ANSWER_MS = 3_000;
/** The person takes their time over the OS's question. */
const ASK_MS = 5 * 60_000;

export function appNoticeHost(): AppNoticeHost | null {
  if (typeof window === "undefined") return null;
  const host = createWindowShellHost(window);
  return host?.notices ? (host as AppNoticeHost) : null;
}

export function readAppNotices(
  host: AppNoticeHost,
  options: { ask?: boolean } = {},
): Promise<ShellNoticesState | null> {
  return new Promise((resolve) => {
    const stop = host.subscribe((message) => {
      if (message.type !== "notices") return;
      clearTimeout(timer);
      stop();
      const { type: _, ...state } = message;
      resolve(state);
    });
    const timer = setTimeout(
      () => {
        stop();
        resolve(null);
      },
      options.ask ? ASK_MS : ANSWER_MS,
    );
    host.post({
      version: SHELL_CONTRACT_VERSION,
      type: "notices",
      ...(options.ask ? { ask: true } : {}),
    });
  });
}

/** Hears how the app's notifications stand whenever it says so on its own. Returns stop. */
export function onAppNotices(
  host: AppNoticeHost,
  listener: (state: ShellNoticesState) => void,
): () => void {
  return host.subscribe((message) => {
    if (message.type !== "notices") return;
    const { type: _, ...state } = message;
    listener(state);
  });
}
